export interface SignageSourceBinding {
  readonly key: string;
  readonly source_type: string;
  readonly status: 'available' | 'empty' | 'unavailable' | string;
  readonly target_block_key?: string | null;
  readonly resolver_key?: string;
  readonly record_count?: number | null;
  readonly privacy_level?: string;
  readonly capabilities?: readonly string[];
}

export interface SignageSceneBlock {
  readonly key: string;
  readonly kind: string;
  readonly source_keys: readonly string[];
  readonly role?: string;
  readonly enabled?: boolean;
}

export interface SignageScene {
  readonly schema_version: string;
  readonly scene_version?: string;
  readonly scene_id: string;
  readonly mode: string;
  readonly layout: string;
  readonly source_bindings: readonly SignageSourceBinding[];
  readonly blocks: readonly SignageSceneBlock[];
}

export interface SignageSceneSummary {
  readonly sceneId: string;
  readonly version: string;
  readonly mode: string;
  readonly blockCount: number;
  readonly sourceCount: number;
  readonly unavailableSourceKeys: readonly string[];
  readonly staleAfterSeconds: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function extractSignageScene(payload: Record<string, unknown>): SignageScene | null {
  const raw = payload['scene'];
  if (!isRecord(raw)) return null;
  if (raw['schema_version'] !== 'signage-scene.v1') return null;
  if (typeof raw['scene_id'] !== 'string') return null;
  if (typeof raw['mode'] !== 'string') return null;
  if (typeof raw['layout'] !== 'string') return null;
  if (!Array.isArray(raw['source_bindings'])) return null;
  if (!Array.isArray(raw['blocks'])) return null;
  return raw as unknown as SignageScene;
}

export function summarizeSignageScene(payload: Record<string, unknown>): SignageSceneSummary | null {
  const scene = extractSignageScene(payload);
  if (!scene) return null;
  const staleRaw = (scene as unknown as Record<string, unknown>)['stale_after_seconds'];
  return {
    sceneId: scene.scene_id,
    version: scene.scene_version ?? scene.schema_version,
    mode: scene.mode,
    blockCount: scene.blocks.length,
    sourceCount: scene.source_bindings.length,
    unavailableSourceKeys: scene.source_bindings
      .filter((source) => source.status === 'unavailable')
      .map((source) => source.key),
    staleAfterSeconds: typeof staleRaw === 'number' ? staleRaw : null,
  };
}
